/* __next_internal_client_entry_do_not_use__ inputBorderStyles,INPUT_HEIGHT,disabledInputStylesNoBorder,inputStylesNoBorder,inputStyles,InputWrapper,FormInputLabel,Input auto */ import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { Row } from "@faire/web--source/slate/Layout";
import { Typography, TypographyStyles } from "@faire/web--source/slate/Typography";
import { HIGH_VIS_FILTER, highVisSlateComponent } from "@faire/web--source/slate/utils/highVisSlateComponent";
import { useUniqueId } from "@faire/web--source/ui/hooks/useUniqueId";
import * as React from "react";
import styled, { css, useTheme } from "styled-components";
import { Spacer } from "../spacing";
import { themeOrDefault } from "../Theme";
import { getDerivedPropsFromErrorProps } from "./error";
var inputBorderFocusStyles = css([
    "&:focus,&:focus-within{border:1px solid ",
    ";}"
], Core.border.default);
export var inputBorderStyles = css([
    "border:1px solid ",
    ";&:invalid{border:1px solid ",
    ";}",
    ";"
], function(props) {
    return props.$showErrorState ? Core.border.critical : Core.border.muted;
}, Core.border.critical, inputBorderFocusStyles);
export var INPUT_HEIGHT = 42;
export var disabledInputStylesNoBorder = css([
    "background-color:",
    ";cursor:default;"
], Core.surface.subdued);
export var inputStylesNoBorder = css([
    "",
    ";appearance:none;border:none;outline:none;padding:0 16px;&:invalid{appearance:none;box-shadow:none;}&::placeholder{color:",
    ";}&:disabled{",
    ";}",
    ";"
], function(props) {
    return TypographyStyles({
        color: props.disabled ? Core.text.subdued : Core.text.default
    });
}, function(param) {
    var theme = param.theme;
    var _themeOrDefault_input_placeholder_color;
    return (_themeOrDefault_input_placeholder_color = themeOrDefault(theme).input.placeholder.color) !== null && _themeOrDefault_input_placeholder_color !== void 0 ? _themeOrDefault_input_placeholder_color : Core.text.subdued;
}, disabledInputStylesNoBorder, function(param) {
    var disabled = param.disabled;
    return disabled ? disabledInputStylesNoBorder : "";
});
export var inputStyles = css([
    "",
    ";",
    ";"
], inputStylesNoBorder, inputBorderStyles);
export var InputWrapper = styled(Row).attrs({
    align: "center"
}).withConfig({
    componentId: "sc-59c1a9e0-0"
})([
    "",
    ";height:",
    "px;padding:0 16px;background:",
    ";cursor:text;",
    ";",
    ""
], function(param) {
    var borderless = param.$borderless;
    return !borderless ? inputBorderStyles : inputBorderFocusStyles;
}, INPUT_HEIGHT, Core.surface.inverse, function(param) {
    var disabled = param.$disabled;
    return disabled ? css([
        "background-color:",
        ";"
    ], Core.surface.subdued) : "";
}, highVisSlateComponent(HIGH_VIS_FILTER.COMPONENT));
var InvisibleInput = styled.input.withConfig({
    componentId: "sc-59c1a9e0-1"
})([
    "",
    ";padding:0;@supports (display:flex){width:0;flex:1 1 auto;}background:none;cursor:inherit;border:0 !important;"
], inputStylesNoBorder);
export var FormInputLabel = styled.label.withConfig({
    componentId: "sc-59c1a9e0-2"
})([
    "",
    ";"
], function(param) {
    var $typography = param.$typography;
    return TypographyStyles($typography !== null && $typography !== void 0 ? $typography : {});
});
var Wrapper = styled.div.withConfig({
    componentId: "sc-59c1a9e0-3"
})([
    "position:relative;flex:",
    ';input::-webkit-outer-spin-button,input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0;}input[type="number"]{-moz-appearance:textfield;}width:100%;'
], function(props) {
    return props.$disableFlex ? "none" : 1;
});
var getAriaLabelProps = function(ariaLabel, labelId) {
    if (ariaLabel) {
        return {
            "aria-label": ariaLabel
        };
    } else if (labelId) {
        return {
            "aria-labelledby": labelId
        };
    } else {
        return {};
    }
};
/**
 * @trackcomponent
 *
 * @link [Docs](https://slate.faire.team/slate/component/input/web/input)
 */ export var Input = /*#__PURE__*/ React.forwardRef(function(_param, ref) {
    var disableFlex = _param.disableFlex, error = _param.error, label = _param.label, tooltip = _param.tooltip, helper = _param.helper, prefixNode = _param.prefixNode, suffixNode = _param.suffixNode, className = _param.className, onClick = _param.onClick, borderless = _param.borderless, id = _param.id, ariaLabel = _param["aria-label"], maxLength = _param.maxLength, hideCharacterCount = _param.hideCharacterCount, _param_isButton = _param.isButton, isButton = _param_isButton === void 0 ? true : _param_isButton, inputBoxRef = _param.inputBoxRef, props = _object_without_properties(_param, [
        "disableFlex",
        "error",
        "label",
        "tooltip",
        "helper",
        "prefixNode",
        "suffixNode",
        "className",
        "onClick",
        "borderless",
        "id",
        "aria-label",
        "maxLength",
        "hideCharacterCount",
        "isButton",
        "inputBoxRef"
    ]);
    var defaultId = useUniqueId("input-");
    var input = useTheme().input;
    var defaultRef = React.useRef(null);
    var innerRef = ref !== null && ref !== void 0 ? ref : defaultRef;
    var _getDerivedPropsFromErrorProps = getDerivedPropsFromErrorProps({
        error: error
    }), showErrorState = _getDerivedPropsFromErrorProps.showErrorState, showErrorMessage = _getDerivedPropsFromErrorProps.showErrorMessage, errorMessage = _getDerivedPropsFromErrorProps.errorMessage;
    var inputId = id !== null && id !== void 0 ? id : defaultId;
    var labelId = label ? "".concat(inputId, "__label") : undefined;
    var _props_value;
    var value = (_props_value = props.value) !== null && _props_value !== void 0 ? _props_value : "";
    var ariaLabelProps = getAriaLabelProps(ariaLabel, labelId);
    var shouldShowCharacterCount = !hideCharacterCount && !!maxLength;
    var _input_label;
    return /*#__PURE__*/ _jsxs(Wrapper, {
        $disableFlex: disableFlex,
        children: [
            label ? /*#__PURE__*/ _jsx(FormInputLabel, {
                id: labelId,
                $typography: _object_spread_props(_object_spread({}, (_input_label = input.label) !== null && _input_label !== void 0 ? _input_label : {}), {
                    color: showErrorState ? Core.text.critical : input.label.color
                }),
                htmlFor: inputId,
                children: tooltip ? /*#__PURE__*/ _jsxs(Row, {
                    children: [
                        label,
                        /*#__PURE__*/ _jsx(Spacer, {
                            size: "1x"
                        }),
                        tooltip
                    ]
                }) : label
            }) : null,
            /*#__PURE__*/ _jsxs(InputWrapper, {
                className: className,
                $disabled: props.disabled,
                $showErrorState: showErrorState,
                onClick: function(e) {
                    var _innerRef_current;
                    innerRef === null || innerRef === void 0 ? void 0 : (_innerRef_current = innerRef.current) === null || _innerRef_current === void 0 ? void 0 : _innerRef_current.focus();
                    if (!props.disabled) {
                        onClick === null || onClick === void 0 ? void 0 : onClick(e);
                    }
                },
                role: isButton ? "button" : "",
                $borderless: borderless,
                ref: inputBoxRef,
                children: [
                    isNotUndefined(prefixNode) ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            typeof prefixNode === "string" ? /*#__PURE__*/ _jsx(Typography, {
                                children: prefixNode
                            }) : prefixNode,
                            /*#__PURE__*/ _jsx(Spacer, {
                                size: 8
                            })
                        ]
                    }) : null,
                    /*#__PURE__*/ _jsx(InvisibleInput, _object_spread_props(_object_spread({
                        ref: innerRef,
                        placeholder: "",
                        maxLength: maxLength
                    }, props, ariaLabelProps), {
                        id: inputId
                    })),
                    isNotUndefined(suffixNode) ? /*#__PURE__*/ _jsx(_Fragment, {
                        children: typeof suffixNode === "string" ? /*#__PURE__*/ _jsx(Typography, {
                            color: props.disabled ? Core.text.disabled : Core.text.default,
                            children: suffixNode
                        }) : suffixNode
                    }) : null
                ]
            }),
            shouldShowCharacterCount || errorMessage || helper ? /*#__PURE__*/ _jsx(FooterSection, {
                errorMessage: errorMessage,
                showErrorMessage: showErrorMessage,
                helper: helper,
                maxLength: maxLength,
                shouldShowCharacterCount: shouldShowCharacterCount,
                value: value
            }) : null
        ]
    });
});
var HelperOrErrorText = function(param) {
    var helper = param.helper, errorMessage = param.errorMessage, showErrorMessage = param.showErrorMessage;
    if (showErrorMessage) {
        return /*#__PURE__*/ _jsx(Typography, {
            variant: "labelSansRegular",
            color: Core.text.critical,
            children: errorMessage
        });
    } else {
        return /*#__PURE__*/ _jsx(Typography, {
            variant: "labelSansRegular",
            color: Core.text.default,
            "data-test-id": "input-helper-message",
            children: helper
        });
    }
};
var FooterSection = function(param) {
    var helper = param.helper, errorMessage = param.errorMessage, showErrorMessage = param.showErrorMessage, maxLength = param.maxLength, shouldShowCharacterCount = param.shouldShowCharacterCount, value = param.value;
    var textColor = showErrorMessage ? Core.text.critical : Core.text.default;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Spacer, {
                size: 4
            }),
            /*#__PURE__*/ _jsxs(Row, {
                justify: "space-between",
                children: [
                    /*#__PURE__*/ _jsx(HelperOrErrorText, {
                        helper: helper,
                        errorMessage: errorMessage,
                        showErrorMessage: showErrorMessage
                    }),
                    shouldShowCharacterCount ? /*#__PURE__*/ _jsxs(Typography, {
                        variant: "labelSansRegular",
                        color: textColor,
                        children: [
                            value === null || value === void 0 ? void 0 : value.length,
                            "/",
                            maxLength
                        ]
                    }) : null
                ]
            })
        ]
    });
};
